/**
 * Race a promise against a timeout, resolving to null on timeout.
 *
 * Unlike a bare `Promise.race([promise, timeoutPromise])`, the internal timer is
 * cleared as soon as the race settles — an uncancelled timer would otherwise fire
 * later and run `onTimeout` (e.g. a "timed out" warning) for an operation that
 * actually succeeded.
 *
 * @param promise The operation to bound.
 * @param ms Timeout in milliseconds.
 * @param onTimeout Called only if the timeout genuinely wins the race.
 */
export async function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
  onTimeout?: () => void,
): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => {
      // Resolve BEFORE the callback: a throwing onTimeout must never leave the
      // race unsettled (it would hang callers whose raced promise is also hung).
      resolve(null);
      onTimeout?.();
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}
